import { styled } from "@mui/material/styles";
import { useState } from "react";
import { FileDrop } from "react-file-drop";
import { useDispatch } from "react-redux";
import { Box, Button, CloudUploadIcon, Divider, Typography } from "../../mui";
import { snackbarActions } from "../../Store/snackbar";
import AcsService from "../../services/AcsService";

const fileTypeMapping = {
  csv: "text/csv",
  xlsx: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  xml: "text/xml",
  xls: "application/vnd.ms-excel",
};

const CustomizedFileDrop = styled(FileDrop)(({ theme }) => ({
  flex: 1,
  "& .file-drop-target": {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
  },
}));

const FileUploadBulkEdit = ({
  files,
  setFiles,
  accept,
  disabled,
  handleRefresh,
  clientState,
  projectState,
  setValue,
  ...props
}) => {
  const dispatch = useDispatch();
  const [hover, setHover] = useState(false);

  const validTypes = accept.map((key) => {
    return fileTypeMapping[key];
  });

  // After droping the file in file upload area this function will execute.
  const handleFileDrop = (files, event) => {
    event.preventDefault();
    const filesValidation = files[0];
    if (!clientState && !projectState) {
      dispatch(
        snackbarActions.showNotification({
          snackbarOpen: true,
          snackbarType: "error",
          snackbarMessage: "Select Client and Project",
        })
      );
      return;
    }
    if (!validTypes.includes(filesValidation.type)) {
      console.log(filesValidation.type);
      dispatch(
        snackbarActions.showNotification({
          snackbarOpen: true,
          snackbarType: "error",
          snackbarMessage: `Invalid File Format, must be of type: ${accept.join(
            ", "
          )}`,
        })
      );
      return;
    }
    AcsService.uploadEditFileValidations(clientState, projectState, files[0])
      .then((resp) => {
        setValue("two");
        handleRefresh();
        dispatch(
          snackbarActions.showNotification({
            snackbarOpen: true,
            snackbarType: "success",
            snackbarMessage: resp.data,
          })
        );
      })
      .catch((err) => {
        setFiles(null);
      });
    setFiles(files[0]);
  };

  // After choosing & uploading the file this function will execute.
  const handleFileUpload = (event) => {
    const filesValidation = event.target.files[0];
    if (!validTypes.includes(filesValidation.type)) {
      console.log("Invalid Type");
      dispatch(
        snackbarActions.showNotification({
          snackbarOpen: true,
          snackbarType: "error",
          snackbarMessage: `Invalid File Format, must be of type: ${accept.join(
            ","
          )}`,
        })
      );
      return;
    }
    AcsService.uploadEditFileValidations(
      clientState,
      projectState,
      event.target.files[0]
    )
      .then((resp) => {
        setValue("two");
        handleRefresh();
        dispatch(
          snackbarActions.showNotification({
            snackbarOpen: true,
            snackbarType: "success",
            snackbarMessage: resp.data,
          })
        );
      })
      .catch((err) => {
        setFiles(null);
        dispatch(
          snackbarActions.showNotification({
            snackbarOpen: true,
            snackbarType: "error",
            snackbarMessage: err.response.data.detail,
          })
        );
      });
    setFiles(event.target.files[0]);
    event.target.value = null;
  };

  return (
    <Box
      sx={{
        display: "flex",
        bgcolor: hover ? "rgb(200,216,280,0.6)" : "rgb(216,229,242,0.5)",
        borderRadius: "5px",
      }}
    >
      <CustomizedFileDrop
        onFrameDragEnter={(event) => setHover(true)}
        onFrameDragLeave={(event) => setHover(false)}
        onFrameDrop={(event) => setHover(false)}
        onDragOver={(event) => {}}
        onDragLeave={(event) => {}}
        onDrop={(files, event) => handleFileDrop(files, event)}
      >
        <Box
          sx={{
            display: "flex",
            flex: 1,
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <CloudUploadIcon
            sx={{
              mt: 2,
              fontSize: 60,
              color: "secondary.light",
            }}
            aria-hidden="true"
          />
          <Typography
            component="div"
            role="status"
            tabIndex={0}
            sx={{
              fontSize: "1rem",
              fontWeight: 500,
              mb: 1,
              padding: "4px 8px",
              borderRadius: "4px",
              color: (theme) =>
                theme.palette.mode === "dark"
                  ? "#ffffff" // Pure white text for maximum contrast
                  : "rgba(0, 0, 0, 0.87)",
              // Add a subtle text shadow in dark mode for better readability
              textShadow: (theme) =>
                theme.palette.mode === "dark"
                  ? "0px 1px 2px rgba(0, 0, 0, 0.5)"
                  : "none",
            }}
            aria-label="Just Drag and Drop"
          >
            Just Drag and Drop
          </Typography>
          <Divider flexItem>
            <Typography
              component="div"
              role="status"
              tabIndex={0}
              sx={{
                fontSize: "1rem",
                fontWeight: 500,
                mb: 1,
                padding: "4px 8px",
                borderRadius: "4px",
                color: (theme) =>
                  theme.palette.mode === "dark"
                    ? "#ffffff" // Pure white text for maximum contrast
                    : "rgba(0, 0, 0, 0.87)",
                // Add a subtle text shadow in dark mode for better readability
                textShadow: (theme) =>
                  theme.palette.mode === "dark"
                    ? "0px 1px 2px rgba(0, 0, 0, 0.5)"
                    : "none",
              }}
              aria-label="or"
            >
              or
            </Typography>
          </Divider>

          <Button
            sx={{
              mb: 2.5,
              mt: 1,
              display: "inline-block",
              opacity: disabled ? 0.7 : 1,
              cursor: disabled ? "not-allowed" : "pointer",
              backgroundColor: disabled
                ? "rgba(0, 0, 0, 0.12)"
                : "primary.main",
              color: disabled ? "rgba(0, 0, 0, 0.26)" : "#fff",
              "&:hover": {
                backgroundColor: disabled
                  ? "rgba(0, 0, 0, 0.12)"
                  : "primary.dark",
              },
              "&:focus": {
                outline: "2px solid",
                outlineColor: "primary.main",
                outlineOffset: "2px",
              },
            }}
            onClick={() => {
              if (!disabled) {
                document.getElementById("file-upload-input").click();
              }
            }}
            onKeyDown={(e) => {
              if ((e.key === "Enter" || e.key === " ") && !disabled) {
                e.preventDefault();
                document.getElementById("file-upload-input").click();
              }
            }}
            variant="contained"
            tabIndex={0}
            aria-disabled={disabled}
            aria-label={
              disabled
                ? "Choose a file - Select project first"
                : "Choose a file"
            }
          >
            Choose a File
            <input
              id="file-upload-input"
              type="file"
              name="file"
              accept=".xlsx"
              hidden
              onChange={handleFileUpload}
              aria-hidden="true"
              tabIndex={-1}
            />
          </Button>
        </Box>
      </CustomizedFileDrop>
    </Box>
  );
};

export default FileUploadBulkEdit;
